import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBusy } from "../context/BusyContext";
import { useMessageModal } from "../admin/contexts/MessageModalContext";
import axios from "axios";
import PolicyModal from "../components/PolicyModal";
import "./css/authstyles.css";
import {
  fetchActivePolicies,
  findPolicyByTitle,
  PolicyItem,
} from "../utils/policyHelpers";

type RegionOption = {
  id: number;
  region_name: string;
  region_code?: string | null;
  country_code?: string | null;
  dataforseo_location_name?: string | null;
  dataforseo_language_code?: string | null;
};

const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;

const SignUp = () => {
  const navigate = useNavigate();
  const { runBusy } = useBusy();
  const { showModal } = useMessageModal();

  const [formData, setFormData] = useState({
    name: "",
    companyName: "",
    email: "",
    phoneNumber: "",
    country: "",
    password: "",
    confirmPassword: "",
    userImage: null as File | null,
  });

  const [previewImage, setPreviewImage] = useState("/images/user-icon.png");
  const [policies, setPolicies] = useState<PolicyItem[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyItem | null>(null);

  const [regions, setRegions] = useState<RegionOption[]>([]);
  const [countrySearch, setCountrySearch] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadPolicies = async () => {
      try {
        const data = await runBusy(() => fetchActivePolicies(BASE_URL));

        if (isMounted) {
          setPolicies(data);
        }
      } catch (error) {
        console.error("Failed to load policies:", error);
      }
    };

    loadPolicies();

    return () => {
      isMounted = false;
    };
  }, [runBusy]);

  useEffect(() => {
    let isMounted = true;

    const fetchRegions = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/regions`, {
          withCredentials: true,
        });

        if (isMounted && response.data?.success) {
          setRegions(response.data.data || []);
        }
      } catch (error) {
        console.error("Failed to fetch regions:", error);
      }
    };

    fetchRegions();

    return () => {
      isMounted = false;
    };
  }, []);

  const openPrivacyPolicy = () => {
    const policy = findPolicyByTitle(policies, "Privacy Policy");
    if (policy) setSelectedPolicy(policy);
  };

  const openTermsPolicy = () => {
    const policy =
      findPolicyByTitle(policies, "Terms of Service") ||
      findPolicyByTitle(policies, "Terms of Services");

    if (policy) setSelectedPolicy(policy);
  };

  const handleChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e: any) => {
    const file = e.target.files[0];

    if (file) {
      setFormData({ ...formData, userImage: file });
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const filteredRegions = regions.filter((region) => {
    const search = countrySearch.toLowerCase();

    return (
      region.region_name?.toLowerCase().includes(search) ||
      region.country_code?.toLowerCase().includes(search) ||
      region.region_code?.toLowerCase().includes(search)
    );
  });

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      showModal({
        type: "error",
        title: "Validation Error",
        message: "Password and Confirm Password do not match.",
        confirmText: "OK",
      });
      return;
    }

    if (!formData.country) {
      showModal({
        type: "error",
        title: "Validation Error",
        message: "Please select your country.",
        confirmText: "OK",
      });
      return;
    }

    const formDataObj = new FormData();

    Object.keys(formData).forEach((key) => {
      const value = (formData as any)[key];

      if (value !== null && value !== undefined) {
        formDataObj.append(key, value);
      }
    });

    try {
      const response = await axios.post(`${BASE_URL}/signup`, formDataObj, {
        withCredentials: true,
      });

      if (response.data?.success === false) {
        showModal({
          type: "error",
          title: "Error",
          message: response.data?.message || "Unable to complete signup.",
          confirmText: "OK",
        });
        return;
      }

      showModal({
        type: "success",
        title: "Success",
        message:
          response.data?.message ||
          "Signup successful, please check you email and click on Activate Account.",
        confirmText: "OK",
        onConfirm: () => {
          navigate("/signin", {
            state: { activationPending: true },
          });
        },
      });
    } catch (error: any) {
      console.error("Signup error:", error);

      showModal({
        type: "error",
        title: "Error",
        message:
          error?.response?.data?.message ||
          "Something went wrong. Please try again.",
        confirmText: "OK",
      });
    }
  };

  return (
    <div className="auth-container">
      <div>
        <img
          style={{
            width: 45,
            position: "absolute",
            marginLeft: -140,
          }}
          src="/images/Lyra-Zone-Logo-New-White.png"
          alt="Lyra Zone Logo"
        />

        <label htmlFor="file-upload" className="attachment-icon">
          <img
            style={{
              width: 35,
              height: 50,
              position: "absolute",
              right: 0,
              marginRight: "-150px",
              cursor: "pointer",
              borderRadius: "50%",
              objectFit: "cover",
            }}
            src={previewImage}
            alt="Upload"
          />
        </label>
      </div>

      <form
        onSubmit={handleSubmit}
        className="auth-bg-white p-6 rounded shadow-md w-96"
      >
        <h2 className="text-2xl font-bold mb-4">Sign Up</h2>

        <input
          type="text"
          name="name"
          placeholder="Full Name"
          onChange={handleChange}
          required
          className="auth-border p-2 w-full mb-2"
        />

        <input
          type="text"
          name="companyName"
          placeholder="Company Name (Optional)"
          onChange={handleChange}
          className="auth-border p-2 w-full mb-2"
        />

        <input
          type="email"
          name="email"
          placeholder="Email"
          onChange={handleChange}
          required
          className="auth-border p-2 w-full mb-2"
        />

        <input
          type="tel"
          name="phoneNumber"
          placeholder="Phone Number"
          onChange={handleChange}
          required
          className="auth-border p-2 w-full mb-2"
        />

        <div className="auth-country-dropdown-wrapper" ref={countryDropdownRef}>
          <input
            type="text"
            name="countrySearch"
            placeholder="Select Country"
            value={countrySearch}
            onChange={(e) => {
              setCountrySearch(e.target.value);
              setShowCountryDropdown(true);
              setFormData({ ...formData, country: "" });
            }}
            onFocus={() => setShowCountryDropdown(true)}
            required
            className="auth-border p-2 w-full mb-2"
          />

          {showCountryDropdown && (
            <div className="auth-country-dropdown">
              {filteredRegions.length > 0 ? (
                filteredRegions.map((region) => (
                  <button
                    type="button"
                    key={region.id}
                    className="auth-country-option"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        country: region.region_name,
                      });
                      setCountrySearch(region.region_name);
                      setShowCountryDropdown(false);
                    }}
                  >
                    <span>{region.region_name}</span>
                    {region.country_code && (
                      <small>{region.country_code}</small>
                    )}
                  </button>
                ))
              ) : (
                <div className="auth-country-empty">No country found.</div>
              )}
            </div>
          )}
        </div>

        <input
          type="password"
          name="password"
          placeholder="Password"
          onChange={handleChange}
          required
          className="auth-border p-2 w-full mb-2"
        />

        <input
          type="password"
          name="confirmPassword"
          placeholder="Confirm Password"
          onChange={handleChange}
          required
          className="auth-border p-2 w-full mb-2"
        />

        <div>
          <input
            type="file"
            id="file-upload"
            name="userImage"
            accept="image/*"
            onChange={handleFileChange}
            style={{ display: "none" }}
          />
        </div>

        <button type="submit" className="sign-in-btn">
          Sign Up
        </button>
      </form>

      <div className="general-text">
        <a style={{ marginLeft: 70 }} href="./signin">
          Already have account !
        </a>
      </div>

      <div className="auth-general-terms">
        By signing up, you agree to our{" "}
        <span className="auth-policy-link" onClick={openPrivacyPolicy}>
          Privacy Policy
        </span>{" "}
        and{" "}
        <span className="auth-policy-link" onClick={openTermsPolicy}>
          Terms of Service
        </span>
        .
      </div>

      <PolicyModal
        isOpen={!!selectedPolicy}
        title={selectedPolicy?.title || ""}
        body={selectedPolicy?.body || ""}
        onClose={() => setSelectedPolicy(null)}
      />
    </div>
  );
};

export default SignUp;
